import React, { useCallback, useMemo } from "react";
import {
  ProductInfoLabel as IProductInfoLabel,
  PricePowerLabelType,
  ProductInfoLabel,
  EnhancedProductCard,
  PromotionLabelType,
} from "@/types/productCard";
import "./index.scss";
import { ProductInfoLabelClassMap, ProductTagLabelMap, ProductTagStyleClassMap } from "./constants";
import { getPriceYuanAndFen } from "./utils";
import { useJumpToPage } from "@/hooks/useJumpToPage";
import Exposure from "@/components/exposure";
import { useTracker } from "@/context/TrackerContext";
import { PRODUCT_CARD_BID_MC, PRODUCT_CARD_BID_MV } from "@/track/distribution-center";
import { getAppEnv } from "@/lib/getBrowserEnv";

// 商品信息标签
const ProductInfoTag: React.FC<{ label: ProductInfoLabel }> = ({ label }) => {
  const labelStyleClass = ProductInfoLabelClassMap[label.type] || "sale-num-tag";
  return (
    <div className={`product-info-tag ${labelStyleClass}`} id={`product-tag-${label.type}`}>
      <span className="tag-label">{label.labelContent}</span>
    </div>
  );
};

// 价格组件
const Price: React.FC<{ price: string; wholeSaleNum?: number }> = ({ price, wholeSaleNum }) => {
  const { priceYuan, priceFen } = useMemo(() => getPriceYuanAndFen(price), [price]);

  return (
    <div className="price">
      <span className="price-symbol">¥</span>
      <span className="price-yuan">{priceYuan}</span>
      {priceFen && <span className="price-fen">.{priceFen}</span>}
      {/* 使用次数 */}
      {wholeSaleNum && wholeSaleNum > 1 ? (
        <span className="usage-count">/{wholeSaleNum}次</span>
      ) : null}
    </div>
  );
};

// 最低价格标签
const PeriodLowestPriceTag: React.FC<{ label: string; discount?: string | null }> = ({
  label,
  discount,
}) => {
  return (
    <div className="period-lowest-price-tag" id="price-tag">
      <img
        className="tag-icon"
        src="https://p0.meituan.net/pepperfe/944d0691a85b74e1c15f2cc88847e70f1089.png"
      />
      <span className="tag-label">
        {label}
        {discount && `·${discount}`}
      </span>
    </div>
  );
};

// 普通标签
const PlainTag: React.FC<{ label: string }> = ({ label }) => {
  return <div className="plain-tag">{label}</div>;
};

interface ProductCardProps {
  productInfo: EnhancedProductCard;
  isSoldOut?: boolean;
  isButtonEnable?: boolean;
  buttonText?: string;
  index: number;
}

// 商卡组件
const ProductCard: React.FC<ProductCardProps> = (props: ProductCardProps) => {
  const { productInfo: info = {} as EnhancedProductCard, index } = props;
  let { isSoldOut, buttonText = "抢", isButtonEnable = true } = props;
  const tracker = useTracker();
  const jumpToPage = useJumpToPage();

  // 灵犀埋点自定义参数
  const lxCustom = useMemo(() => {
    const { type, wholeLabel } = info.availableCouponTag || {};
    // 获取下挂优惠类型，1-可用券，2-商品立减
    let couponType = '';
    if (type === PromotionLabelType.AVAILABLE_COUPON) {
      couponType = '1';
    } else if (type === PromotionLabelType.CUT_DOWN) {
      couponType = '2';
    }
    return {
      sku_id: info.productId || '',
      wm_item_type: '',
      is_uniondish_red: Boolean(couponType), // 是否有最大可用券/页面立减信息
      poi_id: info.poiId || '',
      poi_name: info.poiName || '',
      second_category_id: info.secondCategoryId || '', // 商品二级品类id 1=到餐；2=到综；3=酒店；4=门票，6=医药，7=生活服务，8=拼好饭，9=神枪手
      sku_index: index,
      sku_name: info.skuName || '',
      uniondish_red_text: wholeLabel || '', // 下挂优惠文案
      uniondish_red_type: couponType, // 下挂优惠类型，1-可用券，2-商品立减
    }
  }, [info]);

  const handleProductCardExpose = useCallback(() => {
    if (tracker) {
      tracker.moduleView(PRODUCT_CARD_BID_MV.CARD, lxCustom);
    }
  }, [tracker, lxCustom]);

  const handleProductCardClick = useCallback(() => {
    if (tracker) {
      tracker.moduleClick(PRODUCT_CARD_BID_MC.CARD, lxCustom);
    }
    // sessionId 未绑定时展示 Loading，绑定后自动跳转
    jumpToPage({
      ...info.operation,
      source: 'productCard',
    });
  }, [info, tracker, jumpToPage]);

  if (!info) return null;
  // 商品已售罄
  if (!info.canBuy) {
    isSoldOut = true;
    buttonText = "已抢光";
    isButtonEnable = false;
  }

  return (
    <Exposure onExpose={handleProductCardExpose}>
      <div onClick={handleProductCardClick} className="product-card-container">
        {/* 商卡左 */}
        <div className="product-card-left">
          <div className="product-card-left-content" style={{ opacity: isSoldOut ? 0.5 : 1 }}>
            {/* 商品图片 */}
            <img className="product-img" src={info.image} />
            {/* 供给品牌标签：神抢手、拼好饭 */}
            {info.supplyTagImage && (
              <img className={`supply-tag ${ProductTagStyleClassMap[info.categoryType]}`} src={info.supplyTagImage} />
            )}
            {/* 可用券标签 */}
            {info.availableCouponTag && (
              <div className="available-coupon">
                <div className="coupon-label">
                  <span className="label">{info.availableCouponTag.prefix}</span>
                  <span className="amount">{info.availableCouponTag.amount}</span>
                  <span className="label">{info.availableCouponTag.suffix}</span>
                </div>
              </div>
            )}
          </div>
          {/* 图片上的提示文案 */}
          {isSoldOut && <div className="product-image-tip">已抢光</div>}
        </div>
        {/* 商卡右 */}
        <div className="product-card-right" style={{ opacity: isSoldOut ? 0.5 : 1 }}>
          {/* 商品标题，包括神价标签、品牌名称、商品名称 */}
          <div className="product-title">
            {/* 供给类型标签：外卖、团购、快递 */}
            <span className={`category-tag ${ProductTagStyleClassMap[info.categoryType]} ${!getAppEnv().isIos ? 'category-tag-inline-flex' : ''}`}>
              <span className="category-tag-label">{ProductTagLabelMap[info.categoryType]}</span>
            </span>
            {/* 神价标签 */}
            {/* {info.beforeTitleTag && (
              <img className="before-title-tag" src={info.beforeTitleTag} alt="神价标签" />
            )} */}
            {/* 品牌名称 */}
            {info.brandName && <span className="product-brand-name">{info.brandName}</span>}
            {/* 商品名称 */}
            {info.skuName && <span className="product-sku-name">{info.skuName}</span>}
          </div>

          {/* 第三行：商品信息标签行 */}
          {info.productInfoTagList && info.productInfoTagList.length > 0 && (
            <div className="product-info-tag-row">
              {info.productInfoTagList.map((item: IProductInfoLabel, index: number) => (
                <ProductInfoTag key={index} label={item} />
              ))}
            </div>
          )}

          {/* 第四行：门店地址距离、用户行为标签、营销标签行 */}
          <div className="position-info-marketing-tag-row">
            {/* 门店地址距离 */}
            {(info.distance || info.areaName) && (
              <div className="position-info">
                {info.areaName || ""}
                {info.distance && info.areaName ? "·" : ""}
                {info.distance || ""}
              </div>
            )}
            {/* 用户行为标签、营销标签 */}
            {info.marketingTagList &&
              info.marketingTagList.length > 0 &&
              info.marketingTagList.map((item, index) => (
                <div key={index} className="marketing-tag">
                  <span className="tag-label">{item.labelContent}</span>
                </div>
              ))}
          </div>

          {/* 第五行：价格区域 */}
          <div className="price-area">
            <div className="price-container">
              <div className="price-with-tag-container">
                {/* 价格 + 原价 */}
                <div className="price-info-container">
                  <div className="sale-price">
                    <Price price={info.price} wholeSaleNum={info.wholeSaleNum} />
                  </div>
                  {/* 原价 */}
                  {info.originPrice && <div className="origin-price">¥{info.originPrice}</div>}
                </div>
                {/* 价格标签 */}
                {info.priceTag && (
                  <div className="price-tag">
                    {info.priceTag.type === PricePowerLabelType.PERIOD_LOWEST_PRICE ? (
                      <PeriodLowestPriceTag
                        label={info.priceTag.labelContent}
                        discount={info.priceTag.labelDiscount}
                      />
                    ) : (
                      <PlainTag label={info.priceTag.labelContent} />
                    )}
                  </div>
                )}
              </div>
              {/* 抢购按钮 */}
              <div
                id="buy-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isButtonEnable) {
                    handleProductCardClick();
                  }
                }}
                className={`buy-btn ${isSoldOut ? "forbidden" : ""}`}
              >
                {buttonText}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Exposure>
  );
};

export default ProductCard;
